// ============================================================
// MANIC MEETER — Content assembly
// ============================================================
// Balance lives in types.ts as *_SPECS, wording lives in src/i18n/<locale>/.
// This module marries the two, so the rest of the game sees the same shapes it
// always did — only now they speak whichever language is switched on.
//
// Results are cached per locale: these run inside render loops, and rebuilding
// nine arrays per frame to look up one label would be its own kind of meeting.

import {
  ACHIEVEMENT_SPECS, AchievementDef, AchievementId,
  ColleagueRole, DAILY_PERK_SPECS, DailyPerk, Department, ENDING_SPECS, EndingType,
  GameEnding, MeetingType, PERMANENT_UNLOCK_SPECS, PermanentUnlockDef, PermanentUnlockId,
  PlayerRole, POWERUP_SPECS, PowerupDef, PowerupId, STAT_UPGRADE_SPECS, StatUpgrade,
  StatUpgradeId, VENDOR_SPECS, VendorEncounter, WEEKEND_EVENT_SPECS, WeekendEventDef,
  WeekendEventType,
} from "./types";
import { C } from "./i18n";
import { getLocale, onLocaleChange, type Locale } from "./i18n/locale";

interface ContentCache {
  statUpgrades?: StatUpgrade[];
  powerups?: PowerupDef[];
  perks?: DailyPerk[];
  achievements?: AchievementDef[];
  endings?: Record<EndingType, GameEnding>;
  permanentUnlocks?: PermanentUnlockDef[];
  vendors?: VendorEncounter[];
  weekendEvents?: WeekendEventDef[];
}

const caches = new Map<Locale, ContentCache>();

function cache(): ContentCache {
  const locale = getLocale();
  let entry = caches.get(locale);
  if (!entry) {
    entry = {};
    caches.set(locale, entry);
  }
  return entry;
}

onLocaleChange(() => {
  // Content is keyed by locale, so nothing is stale — but a run that switches
  // back and forth should not grow two dictionaries it never reads again.
  caches.clear();
});

// ---- Labels --------------------------------------------------------------

// A role, meeting or department the locale has never heard of still has to
// print as something. Its own id is the least surprising fallback.
export function colleagueRoleLabel(role: ColleagueRole): string {
  return C().colleagueRoles[role] ?? role;
}

export function playerRoleLabel(role: PlayerRole): string {
  return C().playerRoles[role] ?? role;
}

export function departmentLabel(department: Department): string {
  return C().departments[department] ?? department;
}

export function meetingTypeLabel(type: MeetingType): string {
  return C().meetingTypes[type]?.name ?? type;
}

export function meetingTypeDescription(type: MeetingType): string {
  return C().meetingTypes[type]?.description ?? "";
}

export function weekendEventLabel(type: WeekendEventType): string {
  return C().weekendEvents[type]?.name ?? type;
}

// ---- Tables --------------------------------------------------------------

export function statUpgrades(): StatUpgrade[] {
  const c = cache();
  if (!c.statUpgrades) {
    const text = C().statUpgrades;
    c.statUpgrades = STAT_UPGRADE_SPECS.map((spec) => ({ ...spec, ...text[spec.id] }));
  }
  return c.statUpgrades;
}

export function powerupDefs(): PowerupDef[] {
  const c = cache();
  if (!c.powerups) {
    const text = C().powerupTexts;
    c.powerups = POWERUP_SPECS.map((spec) => ({ ...spec, ...text[spec.id] }));
  }
  return c.powerups;
}

export function powerupDef(id: PowerupId): PowerupDef | undefined {
  return powerupDefs().find((p) => p.id === id);
}

export function dailyPerks(): DailyPerk[] {
  const c = cache();
  if (!c.perks) {
    const text = C().perks;
    c.perks = DAILY_PERK_SPECS.map((spec) => ({ ...spec, ...text[spec.id] }));
  }
  return c.perks;
}

export function dailyPerk(id: string): DailyPerk | undefined {
  return dailyPerks().find((p) => p.id === id);
}

export function achievementDefs(): AchievementDef[] {
  const c = cache();
  if (!c.achievements) {
    const text = C().achievements;
    c.achievements = ACHIEVEMENT_SPECS.map((spec) => ({ ...spec, ...text[spec.id] }));
  }
  return c.achievements;
}

export function achievementDef(id: AchievementId): AchievementDef | undefined {
  return achievementDefs().find((a) => a.id === id);
}

export function endings(): Record<EndingType, GameEnding> {
  const c = cache();
  if (!c.endings) {
    const text = C().endings;
    c.endings = Object.fromEntries(
      Object.values(ENDING_SPECS).map((spec) => [spec.type, { ...spec, ...text[spec.type] }]),
    ) as Record<EndingType, GameEnding>;
  }
  return c.endings;
}

export function ending(type: EndingType): GameEnding {
  return endings()[type];
}

export function permanentUnlocks(): PermanentUnlockDef[] {
  const c = cache();
  if (!c.permanentUnlocks) {
    const text = C().permanentUnlocks;
    c.permanentUnlocks = PERMANENT_UNLOCK_SPECS.map((spec) => ({ ...spec, ...text[spec.id] }));
  }
  return c.permanentUnlocks;
}

export function permanentUnlock(id: PermanentUnlockId): PermanentUnlockDef | undefined {
  return permanentUnlocks().find((u) => u.id === id);
}

export function vendorEncounters(): VendorEncounter[] {
  const c = cache();
  if (!c.vendors) {
    const text = C().vendors;
    const powerups = C().powerupTexts;
    c.vendors = VENDOR_SPECS.map((spec) => {
      const vendor = text[spec.id];
      return {
        id: spec.id,
        emoji: spec.emoji,
        name: vendor.name,
        intro: vendor.intro,
        items: spec.items.map((item) => ({
          powerupId: item.powerupId,
          salaryCost: item.salaryCost,
          label: `${vendor.itemLabels[item.powerupId] ?? powerups[item.powerupId].name} — ${item.salaryCost}€`,
        })),
      };
    });
  }
  return c.vendors;
}


export function weekendEvents(): WeekendEventDef[] {
  const c = cache();
  if (!c.weekendEvents) {
    const text = C().weekendEvents;
    c.weekendEvents = WEEKEND_EVENT_SPECS.map((spec) => ({ ...spec, ...text[spec.type] }));
  }
  return c.weekendEvents;
}
